import React, { useCallback, useEffect, useState } from 'react';
import {
  Check, Gift, Info, Wallet, X, Loader2,
} from 'lucide-react';
import client from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import './bonusWalletPanel.css';

/**
 * Bonus Cüzdanı penceresi — bütün şemalar aynı bileşeni açar.
 *
 * Bonus Wallet modeli (06.10.2026): cüzdanda aynı anda **tek** bonus vardır ve
 * pencere yalnızca onu gösterir:
 *   - ACTIVE       → Bonus Bakiyesi, Durum: Çevrimi Devam Ediyor, Kalan Çevrim
 *   - WITHDRAWABLE → Bonus Bakiyesi, Durum: Çekilebilir
 *   - EMPTY        → bonus yok
 * Altında bahsin düşeceği cüzdan seçilir (Ana / Bonus). Seçimi yalnızca
 * oyuncu değiştirir; boş bonus cüzdanı seçilemez (sunucu da reddeder).
 * Durum ve tutarlar sunucudan (`GET /auth/wallet`) gelir; istemci hesaplamaz.
 */
export const BONUS_WALLET_EVENT = 'bonus-wallet:open';

export const openBonusWallet = () => {
  window.dispatchEvent(new CustomEvent(BONUS_WALLET_EVENT));
};

/** Bakiye kutusunu tıklanabilir yapan ortak özellikler. */
export const bonusWalletTrigger = {
  role: 'button',
  tabIndex: 0,
  title: 'Bonus Cüzdanı',
  style: { cursor: 'pointer' },
  onClick: openBonusWallet,
  onKeyDown: (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openBonusWallet();
    }
  },
};

const money = (value) => `${Number(value || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} ₺`;

const STATUS_TEXT = {
  active: 'Çevrimi Devam Ediyor',
  withdrawable: 'Çekilebilir',
};

// Bonus parasıyla açılmış tur sonuç bekliyorsa ve çevrim bitmişse (ya da
// bakiye 0 iken yaşam döngüsü yalnız tur sonucunu bekliyorsa) "çevrim sürüyor"
// denmez: ekonomik bakiye ile sonuçlanmamış tur ayrı gösterilir.
export const statusText = (state, status) => {
  if (state?.round_pending && (Number(state.wager_remaining || 0) <= 0 || Number(state.bonus || 0) <= 0)) {
    return 'Açık bonus turunun sonuçlanması bekleniyor';
  }
  return STATUS_TEXT[status];
};

const Row = ({ label, value, testId }) => (
  <div className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--pusu-border)' }}>
    <span className="text-sm text-gray-400">{label}</span>
    <span className="text-sm font-semibold text-white" data-testid={testId}>{value}</span>
  </div>
);

const BonusWalletPanel = () => {
  const { user, selectWallet } = useAuth();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Site içi iptal onayı (tarayıcı confirm'i kullanılmaz).

  const load = useCallback(async () => {
    setError('');
    try {
      const { data } = await client.get('/auth/wallet');
      setState(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Cüzdan bilgisi alınamadı');
    }
  }, []);

  useEffect(() => {
    const onOpen = () => { setOpen(true); setState(null); load(); };
    window.addEventListener(BONUS_WALLET_EVENT, onOpen);
    return () => window.removeEventListener(BONUS_WALLET_EVENT, onOpen);
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Bakiye değişince (spin, iptal, çekim) açık pencere tazelenir.
  useEffect(() => {
    if (open) load();
  }, [open, load, user?.balance, user?.bonus_balance]);

  if (!open || !user) return null;

  const choose = async (wallet) => {
    setBusy(true);
    setError('');
    try {
      setState(await selectWallet(wallet));
    } catch (e) {
      setError(e.response?.data?.detail || 'Cüzdan değiştirilemedi');
    } finally {
      setBusy(false);
    }
  };

  const status = state?.bonus_status;
  const selected = state?.selected_wallet || 'cash';
  // Betrun (26.10.2026, sistem sahibinin referans görseli): turkuaz çerçeve,
  // ikon kutulu başlık, onay halkalı cüzdan kartları, bilgi kutusu
  // (bonusWalletPanel.css). Diğer şemalar bugünkü pencereyi korur.
  const betrun = typeof document !== 'undefined'
    && document.documentElement.getAttribute('data-site-theme') === 'betrun';

  return (
    <div className="bonus-wallet-panel fixed inset-0 z-[90] flex items-center justify-center px-4"
         style={{ background: 'rgba(0,0,0,0.6)' }}
         onClick={() => setOpen(false)}>
      <div className={betrun ? 'bwp' : 'w-full max-w-sm rounded-2xl p-5'}
           role="dialog" aria-modal="true" aria-label="Bonus Cüzdanı"
           style={betrun ? undefined : { background: 'var(--pusu-card, #14111c)', border: '1px solid var(--pusu-border, #2a2533)' }}
           onClick={(event) => event.stopPropagation()}>
        {betrun ? (
          <div className="bwp-head">
            <span className="bwp-head-icon" aria-hidden="true"><Gift size={22} strokeWidth={1.9} /></span>
            <span className="bwp-title">Bonus Cüzdanı</span>
            <button type="button" onClick={() => setOpen(false)} aria-label="Kapat" className="bwp-close">
              <X size={20} strokeWidth={2.2} />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Gift size={18} className="text-yellow-400" />
              <span className="font-bold text-white">Bonus Cüzdanı</span>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Kapat" className="text-gray-400 hover:text-white">
              <X size={18} />
            </button>
          </div>
        )}

        {!state && !error && (
          <div className="flex justify-center py-6"><Loader2 size={22} className="animate-spin text-gray-400" /></div>
        )}

        {state && status === 'empty' && state.round_pending && (
          <p className="text-xs text-amber-200 mb-3" data-testid="bonus-wallet-round-pending">
            Açık bonus turunun sonuçlanması bekleniyor; tur sonuçlanınca yeni bonus kullanılabilir.
          </p>
        )}

        {state && (status === 'active' || status === 'withdrawable') && (
          <div className="mb-4" data-testid="bonus-wallet-current">
            {state.bonus_title && <div className="text-xs text-gray-500 mb-1">{state.bonus_title}</div>}
            <Row label="Bonus Bakiyesi" value={money(state.bonus)} testId="bonus-wallet-balance" />
            {state.usage_scope && <Row label="Geçerli Olduğu Yer" value={state.usage_scope} testId="bonus-wallet-scope" />}
            <Row label="Durum" value={statusText(state, status)} testId="bonus-wallet-status" />
            {status === 'active' && (
              <Row
                label="Kalan Çevrim"
                value={Number(state.wager_remaining || 0) > 0 ? money(state.wager_remaining) : 'Tamamlandı'}
                testId="bonus-wallet-remaining"
              />
            )}
          </div>
        )}
        {state?.freespin_open && (
          <p className="mb-3 text-[11px] text-sky-300" data-testid="bonus-wallet-freespin">
            Freespin hakkınız devam ediyor{state.freespin_title ? ` (${state.freespin_title})` : ''}; kazancı bu cüzdana yazılır.
          </p>
        )}

        {state && status === 'empty' && (
          <div className={betrun ? 'bwp-empty' : 'mb-4 text-sm text-gray-400'} data-testid="bonus-wallet-empty">
            Bonus cüzdanınızda bonus yok.
          </div>
        )}

        {state && (
          <div>
            <div className={betrun ? 'bwp-label' : 'text-xs text-gray-500 mb-2'}>Bahisleriniz şu cüzdandan düşer:</div>
            <div className={betrun ? 'bwp-opts' : 'grid grid-cols-2 gap-2'}>
              {[
                { key: 'cash', label: 'Ana Cüzdan', amount: state.cash, icon: Wallet },
                { key: 'bonus', label: 'Bonus Cüzdanı', amount: state.bonus, icon: Gift },
              ].map(({ key, label, amount, icon: Icon }) => {
                const active = selected === key;
                const disabled = busy || active || (key === 'bonus' && Number(state.bonus) <= 0);
                return (
                  <button
                    key={key}
                    type="button"
                    disabled={disabled}
                    aria-pressed={active}
                    data-testid={`wallet-select-${key}`}
                    onClick={() => choose(key)}
                    className={betrun ? `bwp-opt ${active ? 'is-active' : ''}` : 'rounded-xl px-3 py-2.5 text-left transition'}
                    style={betrun ? undefined : {
                      border: `1px solid ${active ? 'var(--pusu-gold, #f5c542)' : 'var(--pusu-border, #2a2533)'}`,
                      background: active ? 'rgba(245,197,66,0.08)' : 'transparent',
                      opacity: disabled && !active ? 0.5 : 1,
                    }}
                  >
                    <div className={betrun ? 'bwp-opt-label' : 'flex items-center gap-1.5 text-xs text-gray-400'}>
                      <Icon size={betrun ? 16 : 13} /> {label}
                    </div>
                    <div className={betrun ? 'bwp-opt-amount' : 'text-sm font-bold text-white'}>{money(amount)}</div>
                    {active && <div className={betrun ? 'bwp-opt-selected' : 'text-[10px] text-yellow-400 mt-0.5'}>Seçili</div>}
                    {betrun && (
                      <span className="bwp-radio" aria-hidden="true">{active && <Check size={14} strokeWidth={3} />}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <p className={betrun ? 'bwp-info' : 'text-[11px] text-gray-500 mt-3 leading-relaxed'}>
              {betrun && <span className="bwp-info-icon" aria-hidden="true"><Info size={16} strokeWidth={2.4} /></span>}
              <span>
                Bahis yalnızca seçili cüzdandan düşer; iki cüzdan bir bahiste birleşmez.
                Açık bir oyunda cüzdan değiştirirseniz oyunu yeniden açın.
              </span>
            </p>
          </div>
        )}

        {error && <div className="text-xs text-red-400 mt-3">{String(error)}</div>}
      </div>
    </div>
  );
};

export default BonusWalletPanel;
